import React from 'react';
import { Box, Typography, Chip, Avatar } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

export interface RatingSummary {
  title: string;
  avgRating: number;
  count: number;
}

interface RatingExtremesCardProps {
  ratingSummary: RatingSummary;
  variant: 'top' | 'bottom';
  rank: number;
}

export const RatingExtremesCard = ({ ratingSummary, variant, rank }: RatingExtremesCardProps) => {
  const isTop = variant === 'top';
  const color = isTop ? 'success' : 'error';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        borderRadius: 3,
        border: 1,
        borderColor: `${color}.light`,
        bgcolor: isTop ? 'success.50' : 'error.50',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 2,
          borderColor: `${color}.main`,
        },
      }}
    >
      <Avatar
        sx={{
          width: 36,
          height: 36,
          bgcolor: isTop ? 'success.light' : 'error.light',
          color: `${color}.dark`,
          fontWeight: 700,
          fontSize: 14,
          fontFamily: "'Space Grotesk', sans-serif",
        }}
        variant="rounded"
      >
        #{rank}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 600,
            fontSize: 15,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {ratingSummary.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {ratingSummary.count} review{ratingSummary.count !== 1 ? 's' : ''}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Chip
          icon={<StarIcon sx={{ fontSize: 16 }} />}
          label={ratingSummary.avgRating.toFixed(1)}
          color={color}
          size="small"
          sx={{ fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif" }}
        />
        {isTop ? (
          <TrendingUpIcon color="success" fontSize="small" />
        ) : (
          <TrendingDownIcon color="error" fontSize="small" />
        )}
      </Box>
    </Box>
  );
};
